import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import cn from "classnames";
import { differenceInHours } from "date-fns/esm";
import { useIntl } from "react-intl";

import ActivityStatus from "@mapmycustomers/shared/enum/activity/ActivityStatus";
import { Activity, EntityType } from "@mapmycustomers/shared/types/entity";
import PostponedDateGetter from "@mapmycustomers/shared/types/entity/activities/PostponedDateGetter";
import { stopEvents } from "@mapmycustomers/shared/util/browser";

import PostponeButton from "@app/component/activity/PostponeButton";
import ToggleCompleteButton, {
  getActivityCompletedActionName,
} from "@app/component/activity/ToggleCompleteButton";
import { getPostponeMenuItems } from "@app/component/activity/utils/postpone";
import MoreButton from "@app/component/MoreButton";
import { activityListViewAnalytics } from "@app/scene/activity/activityTableAnalytics";
import ActivityMenu from "@app/scene/activity/component/ActivityMenu";
import {
  deleteActivity,
  fetchList,
  openDuplicationModal,
  toggleComplete,
  updateActivity,
} from "@app/scene/activity/store/actions";
import {
  getDeleteLoadingIds,
  getToggleCompleteLoadingIds,
  getUpdateActivityLoadingIds,
} from "@app/scene/activity/store/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import { ACTIVITY_TIME_DIFFERENCE_ALERT } from "@app/util/activity/const";
import getActivityStatus from "@app/util/activity/getActivityStatus";
import postponeActivity from "@app/util/activity/postponeActivity";
import showActivityTimeUpdateAlert from "@app/util/activity/showActivityTimeUpdateAlert";
import isEmailActivity from "@app/util/email/isEmailActivity";
import { activityLayoutModel } from "@app/util/layout/impl";
import { parseApiDate } from "@app/util/parsers";

import styles from "./SingleActions.module.scss";

interface Props {
  activity: Activity;
  deleteActivity: typeof deleteActivity.request;
  deleteLoadingIds: Activity["id"][];
  fetchList: typeof fetchList.request;
  onEdit: typeof showEntityView;
  openDuplicationModal: typeof openDuplicationModal.request;
  toggleComplete: typeof toggleComplete.request;
  toggleCompleteLoadingIds: Activity["id"][];
  updateActivity: typeof updateActivity.request;
  updateActivityLoadingIds: Activity["id"][];
}

const SimpleActions: React.FC<Props> = ({
  activity,
  deleteActivity,
  deleteLoadingIds,
  fetchList,
  onEdit,
  openDuplicationModal,
  toggleComplete,
  toggleCompleteLoadingIds,
  updateActivity,
  updateActivityLoadingIds,
}) => {
  const intl = useIntl();

  const buttonsContainer = useRef<HTMLDivElement>(null);
  const [hiddenItemsMap, setHiddenItemsMap] = useState<Record<string, boolean>>({});
  const [visible, setVisible] = useState<boolean>(false);

  const hasMissingRequiredFields = useMemo(
    () => activityLayoutModel.hasMissingRequiredFieldsFor(activity),
    [activity]
  );

  const handleCompleteActionToggle = useCallback(() => {
    if (!activity.completed && hasMissingRequiredFields) {
      onEdit({
        edit: true,
        editValues: {
          completed: true,
        },
        entityId: activity.id,
        entityType: EntityType.ACTIVITY,
      });
    } else {
      const startAt = activity?.startAt ? parseApiDate(activity.startAt) : undefined;
      const endAt = activity?.endAt ? parseApiDate(activity.endAt) : undefined;

      if (
        !activity.completed &&
        startAt &&
        Math.abs(differenceInHours(Date.now(), endAt ?? startAt)) >= ACTIVITY_TIME_DIFFERENCE_ALERT
      ) {
        showActivityTimeUpdateAlert(intl, activity, toggleComplete);
      } else {
        toggleComplete(activity);
      }
      activityListViewAnalytics.clicked([
        activity.completed
          ? "Mark Activity Not done yet in Table Row"
          : "Mark Activity Done in Table Row",
      ]);
    }
  }, [activity, hasMissingRequiredFields, intl, onEdit, toggleComplete]);

  const handleEditClick = useCallback(() => {
    onEdit({
      entityId: activity.id,
      entityType: EntityType.ACTIVITY,
      focusedFieldName: "startAt",
    });
  }, [activity.id, onEdit]);

  const handleDeleteActivity = useCallback(
    (payload) => {
      setVisible(false);
      deleteActivity({
        ...payload,
        callback: () => {
          fetchList({ request: {} });
        },
      });
    },
    [deleteActivity, fetchList, setVisible]
  );

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    setHiddenItemsMap((previous) =>
      entries.reduce(
        (result, entry) => ({
          ...result,
          // we know that key is defined since we set it ourselves in this component
          [(entry.target as HTMLElement).dataset.key!]: !entry.isIntersecting,
        }),
        previous
      )
    );
  }, []);

  const handleOpenDuplicationModal = useCallback(
    (id: Activity["id"]) => {
      setVisible(false);
      openDuplicationModal(id);
    },
    [openDuplicationModal, setVisible]
  );

  useEffect(() => {
    if (!buttonsContainer.current) {
      return;
    }

    const observer = new IntersectionObserver(handleIntersection, {
      root: buttonsContainer.current,
      threshold: 1,
    });
    Array.from(buttonsContainer.current.children).forEach((item) => {
      observer.observe(item as Element);
    });
    return () => {
      observer.disconnect();
    };
  }, [handleIntersection]);

  const handlePostpone = useCallback(
    (postponedDateGetter: PostponedDateGetter) => {
      updateActivity(postponeActivity(activity, postponedDateGetter));
    },
    [activity, updateActivity]
  );

  return (
    <div className={styles.actionsContainer} onClick={stopEvents}>
      <div className={styles.buttonsContainer} ref={buttonsContainer}>
        <div
          className={cn({ [styles.invisible]: hiddenItemsMap["toggle-complete-button"] })}
          data-key="toggle-complete-button"
        >
          <ToggleCompleteButton
            activity={activity}
            disabled={isEmailActivity(activity)}
            toggleComplete={handleCompleteActionToggle}
            toggleCompleteLoadingIds={toggleCompleteLoadingIds}
            tooltip={
              isEmailActivity(activity)
                ? intl.formatMessage({
                    id: "activityList.actions.menu.toggleComplete.disabledTooltip.done",
                    defaultMessage:
                      "Auto Email activities can’t be undone because they are logged once an email is successfully sent.",
                    description: "Email activities cannot be scheduled for done action",
                  })
                : undefined
            }
          />
        </div>
        {!activity.completed && (
          <div
            className={cn({ [styles.invisible]: hiddenItemsMap["postpone-button"] })}
            data-key="postpone-button"
          >
            <PostponeButton
              activity={activity}
              analyticIssuer={activityListViewAnalytics}
              className={cn(styles.postponeButton)}
              loading={updateActivityLoadingIds.includes(activity.id)}
              onSelectAnotherDate={handleEditClick}
              onUpdate={updateActivity}
            />
          </div>
        )}
      </div>
      <Dropdown
        onVisibleChange={setVisible}
        overlay={
          <ActivityMenu
            activity={activity}
            deleteActivity={handleDeleteActivity}
            deleteLoadingIds={deleteLoadingIds}
            extraActions={[
              ...(hiddenItemsMap["toggle-complete-button"]
                ? [
                    <Menu.Item
                      disabled={isEmailActivity(activity)}
                      key="complete"
                      onClick={handleCompleteActionToggle}
                    >
                      {getActivityCompletedActionName(intl, activity.completed)}
                    </Menu.Item>,
                  ]
                : []),
              ...(hiddenItemsMap["postpone-button"]
                ? [
                    <Menu.SubMenu
                      key="postpone"
                      title={intl.formatMessage({
                        id: "activityList.actions.menu.postpone",
                        defaultMessage: "Postpone",
                        description: "Activity list item menu - postpone",
                      })}
                    >
                      {getPostponeMenuItems(
                        intl,
                        getActivityStatus(activity) === ActivityStatus.OVERDUE,
                        handlePostpone
                      ).map(({ label, onClick }) => (
                        <Menu.Item key={label} onClick={onClick}>
                          {label}
                        </Menu.Item>
                      ))}
                    </Menu.SubMenu>,
                  ]
                : []),
            ]}
            openDuplicationModal={handleOpenDuplicationModal}
          />
        }
        trigger={["click"]}
        visible={visible}
      >
        <MoreButton horizontal />
      </Dropdown>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  deleteLoadingIds: getDeleteLoadingIds(state),
  toggleCompleteLoadingIds: getToggleCompleteLoadingIds(state),
  updateActivityLoadingIds: getUpdateActivityLoadingIds(state),
});

const mapDispatchToProps = {
  deleteActivity: deleteActivity.request,
  fetchList: fetchList.request,
  onEdit: showEntityView,
  openDuplicationModal: openDuplicationModal.request,
  toggleComplete: toggleComplete.request,
  updateActivity: updateActivity.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SimpleActions);
